import LinkOffIcon from '@mui/icons-material/LinkOff';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { type JSX, type ReactNode, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { InvoicePreviewPanel } from '~/components/invoices/InvoicePreviewPanel';
import { openInNewTab } from '~/components/rowPreview/RowPreviewPanel';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { getSpendingTableColumns } from '~/components/spending/spendingTableColumns';
import { LiftAITable } from '~/components/table';
import type { InvoiceWithPortfolio, RelatedInvoice } from '~/data/schemas/invoice';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { useFeatureFlags } from '~/utils/featureFlags';
import { canAccess, PROPERTY_LINKS_IN_SPENDING } from '~/utils/role';
import { buildUrlForInvoice } from '~/utils/urlBuilder';

/**
 * The columns the embedded `relatedInvoices[]` projection can actually fill. An
 * allow-list rather than a list of hidden columns, so a column added to Spending later
 * doesn't show up here as an empty one.
 */
const VISIBLE_FIELDS = new Set(['kind', 'number', 'status', 'proposedAmount', 'reviewedAmount']);

interface LinkedInvoicesTableProps {
  relatedInvoices: RelatedInvoice[] | undefined;
  onUnlink: (invoiceId: string) => Promise<void>;
  /** The add control, rendered inside the table above its toolbar. */
  headerContent?: ReactNode;
}

/**
 * The invoices and proposals linked to this one, using `getSpendingTableColumns` so a
 * linked invoice reads identically wherever it appears. The rows come from the
 * invoice's embedded `relatedInvoices[]`, a thin projection, so the columns it cannot
 * fill are hidden rather than left to render blanks.
 */
export function LinkedInvoicesTable({
  relatedInvoices,
  onUnlink,
  headerContent,
}: LinkedInvoicesTableProps) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const preview = useTableRowPreview({ list: 'invoice_linked_invoices', apiRef });
  const [searchParams] = useSearchParams();
  const terms = useLocaleTerms();
  const featureFlags = useFeatureFlags();
  const {
    currencyCode,
    userDetails: { role: userRole },
  } = useContext(UserContext);

  const columns = useMemo(
    () =>
      getSpendingTableColumns({
        currencyCode,
        canFilterProperty: false,
        // Both classes are listed together here; the rows are all in hand, so
        // the grid filters them itself.
        canFilterClass: true,
        canLinkToProperty: canAccess(userRole, PROPERTY_LINKS_IN_SPENDING),
        terms,
        showDisputeManagement: featureFlags.disputeManagement === true,
        additionalColumns: [
          {
            field: 'actions',
            type: 'actions',
            getActions: ({ row }): JSX.Element[] => [
              <GridActionsCellItem
                key="unlink"
                icon={<LinkOffIcon />}
                label="Unlink from this invoice"
                onClick={() => void onUnlink(row.id)}
                showInMenu
              />,
            ],
            width: 70,
            align: 'right',
          },
        ],
      }),
    [currencyCode, userRole, terms, featureFlags.disputeManagement, onUnlink],
  );

  // Built from every column the table can have, not only the ones it has right now. The
  // grid reads this through `initialState`, once, when it mounts, so a column added after
  // that was never in the hidden list and showed: `disputeStatus`, when the
  // `disputeManagement` flag arrives late. `showDisputeManagement` is the only option that
  // changes which fields exist; the rest change how a column filters or renders.
  const columnVisibilityModel = useMemo(
    () =>
      Object.fromEntries(
        getSpendingTableColumns({ currencyCode, terms, showDisputeManagement: true })
          .filter((column) => !VISIBLE_FIELDS.has(column.field))
          .map((column) => [column.field, false]),
      ),
    [currencyCode, terms],
  );

  // The projection's amounts are DRF decimal strings; the spending columns format
  // numbers. No visible column reads the fields the projection is missing.
  const rows = useMemo<InvoiceWithPortfolio[]>(
    () =>
      (relatedInvoices ?? []).map(
        (related) =>
          ({
            ...related,
            proposedAmount: related.proposedAmount != null ? Number(related.proposedAmount) : null,
            reviewedAmount: related.reviewedAmount != null ? Number(related.reviewedAmount) : null,
          }) as unknown as InvoiceWithPortfolio,
      ),
    [relatedInvoices],
  );

  // A new tab rather than a navigation: a linked invoice is opened to compare it against
  // this one.
  const rowHref = useCallback(
    (row: InvoiceWithPortfolio) => buildUrlForInvoice(row.id, searchParams),
    [searchParams],
  );

  return (
    <>
      <LiftAITable<InvoiceWithPortfolio>
        height={tableHeight}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        rowCount={rows.length}
        loading={relatedInvoices === undefined}
        tableHeaderTitle="Linked invoices & proposals"
        headerContent={headerContent}
        getRowId={(row) => row.id}
        rowHref={rowHref}
        initialState={{
          pinnedColumns: { right: ['actions'] },
          columns: { columnVisibilityModel },
        }}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? (
        <InvoicePreviewPanel
          rowPreview={preview.rowPreview}
          onOpenFullPage={(id) => openInNewTab(buildUrlForInvoice(id, searchParams))}
        />
      ) : null}
    </>
  );
}
